/* eslint-disable @typescript-eslint/no-magic-numbers */
import React from 'react';
import { formatTime, getOrCreateAudio } from '../../utils';
import { AudioPlayerProps } from './model';
import { View } from './view';
export type { AudioPlayerProps } from './model';

export const AudioPlayer: React.FC<AudioPlayerProps> = ({ source, onClick, duration, size = 'normal', sx }) => {
  const [isPaused, setIsPaused] = React.useState(true);
  const [progress, setProgress] = React.useState(0);
  const [totalDuration, setTotalDuration] = React.useState<number | null>(duration ?? null);

  const audioRef = React.useRef<HTMLAudioElement | null>(null);

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleEnded = () => {
    setIsPaused(true);
    setProgress(0);
    handleClick();
  };

  const handleTimeUpdate = () => {
    const audio = audioRef.current;
    if (audio && audio.duration && Number.isFinite(audio.duration) && audio.duration > 0) {
      setProgress((audio.currentTime / audio.duration) * 100);
    }
  };

  const handlePlayButtonClick = () => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isPaused) {
      audio.play();
    } else {
      audio.pause();
      handleClick();
    }
    setIsPaused(!isPaused);
  };

  React.useEffect(() => {
    const audio = getOrCreateAudio(source);
    audioRef.current = audio;

    const updateDuration = () => {
      if (Number.isFinite(audio.duration) && audio.duration > 0) {
        setTotalDuration(audio.duration);
      }
    };

    updateDuration();

    audio.addEventListener('loadedmetadata', updateDuration);
    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    if (audio.readyState < 1) {
      audio.load();
    }

    return () => {
      audio.removeEventListener('loadedmetadata', updateDuration);
      audio.addEventListener('timeupdate', handleTimeUpdate);
      audio.addEventListener('ended', handleEnded);
    };
  }, [source]);

  const validatedDuration = ((): number => {
    const durationValue = duration ?? totalDuration ?? 0;
    return Number.isFinite(durationValue) && durationValue >= 0 ? durationValue : 0;
  })();

  const viewProps = {
    sx,
    size,
    onPlay: handlePlayButtonClick,
    audioDuration: formatTime(validatedDuration),
    progress,
    isPaused,
  };

  return <View duration={totalDuration ?? 0} {...viewProps} />;
};
